import { Component, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { ProjectService } from '../../Services/project.service';
import { TokenEncryptionService } from '../../Services/token-encryption.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { time24FormatValidator } from 'src/app/utils/time24.validator';
import { globalSettingsTime } from 'src/app/config';
import { EntitlementsService } from 'src/app/Services/entitlements.service';
import { resolveScreenshotIntervalFloorMinutes } from 'src/app/entitlements/screenshot-interval-limit.util';
import { EntitlementKey } from 'src/app/entitlements/entitlement-keys';
import { SettingsEntitlementsService } from 'src/app/Services/settings-entitlements.service';
import { CompanyAddOnEntitlement } from 'src/app/entitlements/settings-entitlements.types';

export interface SelectableEntity {
  id: string;
  name: string;
  role?: string;
  team_lead?: string;
  users?: any[];
  selected?: boolean;
  profile_image_url?: string;
}

@Component({
    selector: 'app-bulk-update-settings',
    templateUrl: './bulk-update-settings.component.html',
    styleUrls: ['./bulk-update-settings.component.css'],
    standalone: false
})
export class BulkUpdateSettingsComponent implements OnInit, OnDestroy {
  // Expose for template
  FormControl = FormControl;
  Math = Math;

  // RBAC Variables
  currentUser: any;
  isAdmin = false;
  isManager = false;
  isLead = false;

  // Selection Variables
  entityType: 'employee' | 'team' = 'employee';
  allEntities: SelectableEntity[] = [];
  filteredEntities: SelectableEntity[] = [];
  // When entityType === 'team', these are the members for the selected team
  teamMembersEntities: SelectableEntity[] = [];
  selectedTeamId: string | null = null;
  // support multi-team selection
  selectedTeamIds: Set<string> = new Set();
  selectAllTeams = false;
  // cache user list for resolving team members
  cachedUsers: any[] = [];
  selectedEntities: Set<string> = new Set();
  searchTerm$ = new Subject<string>();
  searchTerm = '';
  selectAllChecked = false;
  isLoading = false;
  isLoadingEmployees = false;

  // Settings Form
  settingsForm: FormGroup;
  defaultSettings: any = {};
  initialFormValue: any = {};
  isDirty = false;
  isSubmitting = false;
  allEmployeeList: any = [];
  allTeamsList: any = [];

  // Pagination/Virtualization
  displayedItems: SelectableEntity[] = [];
  pageSize = 50;
  currentPage = 0;
  selectedTeam: any = null;
  globalSettingsData: any;
  teamsData: any;
  employeesData: any;
  isWebcamCaptureEntitled = false;
  /** False until add-on assignment has loaded. Avoids forcing the webcam toggle off early. */
  private webcamEntitlementKnown = false;
  companyAddOns: CompanyAddOnEntitlement[] = [];
  companyAddOnLimits: any = {};
  addOnStateMessage = '';

  private destroy$ = new Subject<void>();
  private searchSubscription: Subscription;

  // Settings that track changes
  settingKeys = [
    'ss_periodic',
    'ss_appchange',
    'ss_webcam',
    'ss_screen_capture_time',
    'ss_appchange_interval',
    'ss_idle_time',
    'ss_silent',
    'ss_strict_time_tracking',
    'ss_automatic_tracking',
    'ss_silent'
  ];

  constructor(
    private formBuilder: FormBuilder,
    private projectService: ProjectService,
    private tokenEncryptionService: TokenEncryptionService,
    private snackBar: MatSnackBar,
    private entitlementsService: EntitlementsService,
    private settingsEntitlementsService: SettingsEntitlementsService,
  ) { }

  ngOnInit(): void {
    this.entitlementsService.syncFromSession();
    // preload employees and teams so they can be reused later
    this.getAllEmployees();
    this.getAllTeams();

    this.getGlobalDataSettings();
    this.checkRoleAccess();
    if (!this.isLead) {
      this.loadCurrentUser();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  /**
   * RBAC - Check role access
   */
  checkRoleAccess(): void {
    const user = this.tokenEncryptionService.getEncryptedItem<any>('user');
    if (user && user.role) {
      this.currentUser = user;
      this.isWebcamCaptureEntitled = this.projectService.isEntitlementEnabled(user, EntitlementKey.webcam_capture);
      const roles = Array.isArray(user.role) ? user.role.map(r => r.toLowerCase()) : [user.role.toLowerCase()];

      this.isAdmin = roles.some(r => r.includes('admin') || r.includes('company'));
      this.isManager = roles.includes('manager');
      this.isLead = roles.includes('lead');
    }
  }

  /**
   * Load current user details to determine managed teams/reports
   */
  loadCurrentUser(): void {
    this.projectService.getUser().subscribe(
      (data: any) => {
        this.currentUser = data;
        this.isWebcamCaptureEntitled = this.projectService.isEntitlementEnabled(data, EntitlementKey.webcam_capture);
        this.loadSettingsAddOns(data);
        this.applyEntitlementFieldLocks();
        // Extract managed teams and direct reports if manager
        // if (this.isManager && data.meta) {
        //   console.log("data?.meta", data?.meta);
        //   this.userManagedTeams = (data.meta.managed_teams || []).map(id => String(id));
        //   this.userDirectReports = (data.meta.direct_reports || []).map(id => String(id));
        // }
        // After loading current user, now load entities with proper filtering
        this.loadEntities();
        this.setupSearchListener();
      },
      (error) => {
        console.error('Failed to load current user:', error);
        // Still load entities even if user loading fails
        this.loadEntities();
        this.setupSearchListener();
      }
    );
  }

  /**
   * Load employees or teams based on entityType
   * Uses pre-fetched arrays if available to avoid duplicate HTTP calls.
   */
  loadEntities(): void {
    this.isLoading = true;
    if (this.entityType === 'employee') {
      let entities: SelectableEntity[] = this.allEmployeeList.map((emp: any) => ({
        id: emp.id,
        name: emp.name || emp.email || '',
        role: emp.roles2 || emp.role || '',
        profile_image_url: emp.meta?.profile_image_url || emp.profile_image_url || '',
        selected: false
      }));

      entities = this.applyRoleFilter(entities);
      console.log("Filtered Employees after role filter: ", entities);
      this.allEntities = entities;
      this.filteredEntities = [...entities];
      this.updatePaginatedItems();
      this.isLoading = false;
    } else {
      let teamsData: any = this.allTeamsList.map((team: any) => ({
        id: team.id,
        name: team.name || team.title?.rendered || '',
        team_lead: team.team_lead,
        users: team.users || [],
        meta: team.meta || team, // preserve meta for member parsing
        profile_image_url: team.meta?.profile_image_url || team.profile_image_url || '',
        selected: false
      }));
      teamsData = this.applyRoleFilter(teamsData);
      this.allEntities = teamsData;
      this.filteredEntities = [...teamsData];
      if (this.allEntities && this.allEntities.length > 0) {
        this.selectedTeamId = this.allEntities[0].id;
        this.selectedTeamIds.add(String(this.selectedTeamId));
        this.populateMembersForTeam(this.selectedTeamId);
        this.teamSelected(this.selectedTeamId);
      }
      this.updatePaginatedItems();
      this.isLoading = false;
    }
  }

  /**
   * Apply role-based filtering (Admin: all, Manager: only managed, Lead: restricted)
   */
  applyRoleFilter(entities: SelectableEntity[]): SelectableEntity[] {
    if (this.isAdmin || this.isManager) {
      return entities; // Full access
    }

    return []; // Lead has no access
  }

  /**
   * Setup search listener with debounce
   */
  setupSearchListener(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }

    this.searchSubscription = this.searchTerm$
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(term => {
        this.searchTerm = term;
        this.performSearch();
      });
  }

  /**
   * Search entities by name
   */
  performSearch(): void {
    if (!this.searchTerm.trim()) {
      this.filteredEntities = [...this.allEntities];
    } else {
      const term = this.searchTerm.toLowerCase();
      this.filteredEntities = this.allEntities.filter(entity =>
        entity.name.toLowerCase().includes(term)
      );
    }
    this.currentPage = 0;
    this.updatePaginatedItems();
  }

  /**
   * Handle search input
   */
  onSearchChange(term: string): void {
    this.searchTerm$.next(term);
  }

  /**
   * Update paginated display items
   */
  updatePaginatedItems(): void {
    console.log("this.filteredEntities ::: ", this.filteredEntities);
    this.displayedItems = this.filteredEntities;
    return;
    const start = this.currentPage * this.pageSize;
    const end = start + this.pageSize;
    this.displayedItems = this.filteredEntities.slice(start, end);
  }

  /**
   * Toggle entity selection
   */
  toggleSelection(entityId: string): void {
    if (this.selectedEntities.has(entityId)) {
      this.selectedEntities.delete(entityId);
    } else {
      this.selectedEntities.add(entityId);
    }
    this.updateSelectAllCheckbox();
    this.refreshBulkAddOnApplicability();
  }

  /**
   * Toggle select all checkbox
   */
  toggleSelectAll(): void {
    this.selectAllChecked = !this.selectAllChecked;

    if (this.selectAllChecked) {
      this.filteredEntities.forEach(entity => {
        this.selectedEntities.add(entity.id);
      });
    } else {
      this.filteredEntities.forEach(entity => {
        this.selectedEntities.delete(entity.id);
      });
    }
    this.refreshBulkAddOnApplicability();
  }

  /**
   * Update select all checkbox state
   */
  updateSelectAllCheckbox(): void {
    if (this.displayedItems.length === 0) {
      this.selectAllChecked = false;
      return;
    }
    this.selectAllChecked = this.displayedItems.every(item => this.selectedEntities.has(item.id));
  }

  /**
   * Check if entity is selected
   */
  isSelected(entityId: string): boolean {
    return this.selectedEntities.has(entityId);
  }

  /**
   * Create settings form with all configurable fields
   */
  createSettingsForm(): void {
    const checkingIsValue = (valuePass) => {
      return valuePass != null && valuePass != undefined;
    }

    const typeCastToBoolean = (value) => {
      return value == 'true' || value == true;
    }

    let isSilent = checkingIsValue(this.globalSettingsData?.ss_silent) ? typeCastToBoolean(this.globalSettingsData?.ss_silent) : false;

    this.settingsForm = this.formBuilder.group({
      ss_periodic: new FormControl(checkingIsValue(this.globalSettingsData?.ss_periodic) ? typeCastToBoolean(this.globalSettingsData?.ss_periodic) : true),
      ss_appchange: new FormControl(checkingIsValue(this.globalSettingsData?.ss_appchange) ? typeCastToBoolean(this.globalSettingsData?.ss_appchange) : true),
      ss_webcam: new FormControl({
        value: checkingIsValue(this.globalSettingsData?.ss_webcam)
          ? typeCastToBoolean(this.globalSettingsData?.ss_webcam)
          : true,
        disabled: !this.isWebcamCaptureEntitled
      }),
      ss_screen_capture_time: new FormControl(checkingIsValue(this.globalSettingsData?.ss_screen_capture_time) ? this.globalSettingsData?.ss_screen_capture_time : globalSettingsTime.ss_screen_capture_time, [this.timeValidator, time24FormatValidator()]),
      ss_appchange_interval: new FormControl(checkingIsValue(this.globalSettingsData?.ss_appchange_interval) ? this.globalSettingsData?.ss_appchange_interval : globalSettingsTime.ss_appchange_interval, [this.timeValidator, time24FormatValidator()]),
      ss_idle_time: new FormControl(checkingIsValue(this.globalSettingsData?.ss_idle_time) ? this.globalSettingsData?.ss_idle_time : globalSettingsTime.idle_time, [this.timeValidator, time24FormatValidator()]),
      ss_silent: new FormControl(checkingIsValue(this.globalSettingsData?.ss_silent) ? typeCastToBoolean(this.globalSettingsData?.ss_silent) : false),
      ss_strict_time_tracking: new FormControl(checkingIsValue(this.globalSettingsData?.ss_strict_time_tracking) ? typeCastToBoolean(this.globalSettingsData?.ss_strict_time_tracking) : true),
      ss_automatic_tracking: new FormControl(checkingIsValue(this.globalSettingsData?.ss_automatic_tracking) ? typeCastToBoolean(this.globalSettingsData?.ss_automatic_tracking) : true)
    });

    this.initialFormValue = this.settingsForm.value;
    this.settingsForm.valueChanges.subscribe(() => {
      this.checkIfDirty();
    });
    this.applyEntitlementFieldLocks();
  }

  canBulkSettingsUpdate(): boolean {
    return this.entitlementsService.hasCompany(EntitlementKey.bulk_settings_update);
  }

  canAdvancedTrackingControls(): boolean {
    return this.entitlementsService.hasCompany(EntitlementKey.advanced_tracking_controls);
  }

  canAdvancedScreenshotModes(): boolean {
    return this.entitlementsService.canUseAdvancedScreenshotModes();
  }

  isWebcamEnabled(): boolean {
    const ctrl = this.settingsForm?.get('ss_webcam');
    if (ctrl) {
      return this.parseSettingBoolean(ctrl.value);
    }
    return this.parseSettingBoolean(this.globalSettingsData?.ss_webcam);
  }

  advancedScreenshotModesUpgradeMessage(): string {
    return this.entitlementsService.advancedScreenshotModesUpgradeMessage();
  }

  planScreenshotRetentionLabel(): string {
    const days = this.entitlementsService.getScreenshotsRetentionDays();
    return `Your plan retains screenshots for ${days} days. Activity logs are kept for ${this.entitlementsService.getLogsRetentionDays()} days.`;
  }

  canShortScreenshotInterval(): boolean {
    const addon = this.getCompanyAddOn('custom_periodic_interval');
    if (addon?.enabled && !this.isBulkAddOnInScope('custom_periodic_interval')) {
      return false;
    }

    return this.isBulkAddOnInScope('custom_periodic_interval')
      || this.entitlementsService.hasShortScreenshotIntervalEntitlement();
  }

  minScreenshotCaptureMinutes(): number {
    const user = this.currentUser ?? this.tokenEncryptionService.getEncryptedItem('user');
    const addon = this.getCompanyAddOn('custom_periodic_interval');
    if (addon?.enabled && !this.isBulkAddOnInScope('custom_periodic_interval')) {
      return resolveScreenshotIntervalFloorMinutes(user, false);
    }

    const addOnLimitMinutes = this.settingsEntitlementsService.getAddOnLimitMinutes(addon);
    if (addon?.enabled && this.isBulkAddOnInScope('custom_periodic_interval') && addOnLimitMinutes !== null) {
      return addOnLimitMinutes;
    }

    return resolveScreenshotIntervalFloorMinutes(user, this.canShortScreenshotInterval());
  }

  screenshotIntervalPlanHint(): string {
    const min = this.minScreenshotCaptureMinutes();
    if (this.canShortScreenshotInterval() || min <= 1) {
      return '';
    }
    return `Minimum ${min} minutes on your plan (Professional allows 10 minutes).`;
  }

  webcamBadgeLabel(): string {
    return this.isWebcamCaptureEntitled ? 'Assigned' : 'Not assigned';
  }

  screenshotIntervalBadgeLabel(): string {
    const addon = this.getCompanyAddOn('custom_periodic_interval');
    const min = this.minScreenshotCaptureMinutes();
    if (addon?.enabled && this.isBulkAddOnInScope('custom_periodic_interval')) {
      return `Add-on: ${min}-60 min`;
    }
    return addon?.enabled ? `Core: ${min}-60 min (some not assigned)` : `Core: ${min}-60 min`;
  }

  screenshotIntervalTooltip(): string {
    return `Available screenshot interval: ${this.minScreenshotCaptureMinutes()} to 60 minutes, based on the applicable core or add-on entitlement.`;
  }

  advancedModesBadgeLabel(): string {
    return this.canAdvancedScreenshotModes() ? 'Plan' : 'Plan locked';
  }

  private applyWebcamEntitlementToForm(): void {
    const ssWebcam = this.settingsForm?.get('ss_webcam');
    if (!ssWebcam) {
      return;
    }

    if (ssWebcam.pristine) {
      this.paintWebcamFromBackend(ssWebcam);
    }
    if (this.isWebcamCaptureEntitled && ssWebcam.disabled) {
      ssWebcam.enable({ emitEvent: false });
    }
    if (!this.isWebcamCaptureEntitled && ssWebcam.enabled) {
      ssWebcam.disable({ emitEvent: false });
    }
  }

  /** Put the saved webcam value back if assignment arrived after the form was built. */
  private paintWebcamFromBackend(ctrl: AbstractControl): void {
    const backendValue = this.globalSettingsData?.ss_webcam;
    if (backendValue === undefined || backendValue === null) {
      return;
    }
    const webcamEnabled = this.parseSettingBoolean(backendValue);
    if (ctrl.disabled) {
      ctrl.enable({ emitEvent: false });
    }
    ctrl.setValue(webcamEnabled, { emitEvent: false });
    ctrl.markAsPristine({ onlySelf: true });
    this.initialFormValue = {
      ...this.initialFormValue,
      ss_webcam: webcamEnabled,
    };
  }

  private parseSettingBoolean(value: unknown, defaultValue = false): boolean {
    if (value === undefined || value === null) {
      return defaultValue;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    return String(value).toLowerCase() === 'true';
  }

  /** Webcam toggle is read-only; reflect backend value without allowing edits. */
  private lockWebcamField(): void {
    const ctrl = this.settingsForm?.get('ss_webcam');
    if (!ctrl) {
      return;
    }
    if (!this.webcamEntitlementKnown) {
      if (ctrl.enabled) {
        ctrl.disable({ emitEvent: false });
      }
      return;
    }
    const webcamEnabled = this.isWebcamCaptureEntitled
      ? this.parseSettingBoolean(this.globalSettingsData?.ss_webcam ?? ctrl.value)
      : false;
    if (ctrl.disabled) {
      ctrl.enable({ emitEvent: false });
    }
    ctrl.setValue(webcamEnabled, { emitEvent: false });
    ctrl.disable({ emitEvent: false });
  }

  applyEntitlementFieldLocks(): void {
    if (!this.settingsForm) {
      return;
    }
    if (!this.canAdvancedTrackingControls()) {
      ['ss_strict_time_tracking', 'ss_automatic_tracking'].forEach((key) => {
        const ctrl = this.settingsForm.get(key);
        if (ctrl) {
          ctrl.setValue(false);
          ctrl.disable();
        }
      });
    }
    if (this.isWebcamCaptureEntitled) {
      this.applyWebcamEntitlementToForm();
    } else {
      this.lockWebcamField();
    }
    if (!this.canAdvancedScreenshotModes()) {
      const appChange = this.settingsForm.get('ss_appchange');
      if (appChange) {
        appChange.setValue(false);
        appChange.disable();
      }
    }
  }

  private loadSettingsAddOns(user: any = this.tokenEncryptionService.getEncryptedItem<any>('user')): void {
    const companyId = this.resolveCompanyId(user);
    if (!companyId) {
      this.webcamEntitlementKnown = true;
      this.applyEntitlementFieldLocks();
      return;
    }

    this.settingsEntitlementsService.getCompanyAddOnEntitlements(companyId).subscribe({
      next: (response) => {
        this.companyAddOns = response.assigned_addons ?? response.addons ?? [];
        this.companyAddOnLimits = response.limits ?? {};
        this.addOnStateMessage = '';
        this.webcamEntitlementKnown = true;
        this.refreshBulkAddOnApplicability();
      },
      error: (error) => {
        this.webcamEntitlementKnown = true;
        this.addOnStateMessage = error?.error?.message || 'Unable to load add-on assignment state.';
        this.applyEntitlementFieldLocks();
      },
    });
  }

  private resolveCompanyId(user: any): number {
    const rawCompanyId =
      user?.company_id
      ?? user?.companyId
      ?? user?.meta?.company_id
      ?? user?.meta?.companyId
      ?? user?.id;
    const companyId = Number(rawCompanyId);
    return Number.isFinite(companyId) && companyId > 0 ? companyId : 0;
  }

  private refreshBulkAddOnApplicability(): void {
    this.isWebcamCaptureEntitled = this.isBulkAddOnInScope('webcam_capture');
    this.applyEntitlementFieldLocks();
    this.clampScreenshotIntervalForSave();
  }

  private isBulkAddOnInScope(addonKey: string): boolean {
    const addon = this.getCompanyAddOn(addonKey);
    if (!addon || !(addon.enabled || addon.effective_enabled)) {
      return false;
    }

    if (addon.assignment_mode === 'company' || addon.assignment_mode === 'all_users') {
      return true;
    }

    if (addon.assignment_mode !== 'specific_users' || this.selectedEntities.size === 0) {
      return false;
    }

    const assignedIds = new Set((addon.user_ids ?? []).map((id) => String(id)));
    return Array.from(this.selectedEntities).every((id) => assignedIds.has(String(id)));
  }

  private getCompanyAddOn(addonKey: string): CompanyAddOnEntitlement | undefined {
    return this.companyAddOns.find((addon) => addon.key === addonKey);
  }

  /**
   * Time validator - ensure at least 1 minute
   */
  timeValidator(control: any): { [key: string]: any } | null {
    if (!control.value) return null;
    const value = control.value;
    const [hours, minutes] = value.split(':').map(Number);
    const totalMinutes = (hours * 60) + minutes;
    return totalMinutes < 1 ? { invalidTime: true } : null;
  }

  /**
   * Check if form is dirty
   */
  checkIfDirty(): void {
    const currentValue = this.settingsForm.value;
    this.isDirty = this.settingKeys.some(key => currentValue[key] !== this.initialFormValue[key]);
  }

  /**
   * Check if update button should be enabled
   */
  isUpdateButtonEnabled(): boolean {
    return this.selectedEntities.size > 0 && this.isDirty;
  }

  silentModeToggleChange(event: any) {
    const strictControl = this.settingsForm.get('ss_strict_time_tracking');
    const autoControl = this.settingsForm.get('ss_automatic_tracking');

    if (event.checked) {
      strictControl.setValue(false);
      autoControl.setValue(false);
      strictControl.disable();
      autoControl.disable();
    } else {
      strictControl.enable();
      autoControl.enable();
    }
  }

  /**
   * Format time value for API
   */
  formatTimeValue(timeValue: string): string {
    if (!timeValue) return '';
    const parts = timeValue.split(':');
    if (parts.length === 2) {
      return `${timeValue}:00`;
    }
    return timeValue;
  }

  /**
   * Prepare settings payload
   */
  preparePayload(): any {
    this.clampScreenshotIntervalForSave();
    const formValue = this.settingsForm.getRawValue();
    const payload: any = {};

    this.settingKeys.forEach(key => {
      if (formValue[key] === undefined) {
        return;
      }
      if (key.includes('time') || key === 'ss_appchange_interval' || key === 'ss_screen_capture_time' || key === 'ss_idle_time') {
        payload[key] = formValue[key];
      } else {
        payload[key] = formValue[key];
      }
    });

    if (!this.isWebcamCaptureEntitled) {
      payload['ss_webcam'] = false;
    }

    return payload;
  }

  private clampScreenshotIntervalForSave(): void {
    const ctrl = this.settingsForm?.get('ss_screen_capture_time');
    if (!ctrl) {
      return;
    }

    const min = this.minScreenshotCaptureMinutes();
    const current = this.timeToMinutes(ctrl.value);
    if (current > 0 && current < min) {
      ctrl.setValue(this.minutesToHHMMSS(min), { emitEvent: false });
    }
  }

  private timeToMinutes(value: unknown): number {
    if (!value) {
      return 0;
    }
    const parts = String(value).split(':').map((part) => Number(part));
    if (parts.some((part) => Number.isNaN(part))) {
      return 0;
    }
    return (parts[0] || 0) * 60 + (parts[1] || 0);
  }

  private minutesToHHMMSS(minutes: number): string {
    const safeMinutes = Math.max(1, Math.floor(minutes));
    const hours = Math.floor(safeMinutes / 60);
    const mins = safeMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`;
  }

  /**
   * Submit bulk update
   */
  submitBulkUpdate(): void {
    if (!this.canBulkSettingsUpdate()) {
      return;
    }
    if (!this.settingsForm.valid) {
      return;
    }


    if (Array.from(this.selectedEntities).length <= 0) {
      Swal.fire({
        icon: 'error',
        title: 'Selection Required',
        text: 'Select at least one member from Employees or Teams.',
        timer: 2000,
        showConfirmButton: false
      });
      return;
    }

    let confirmHtml = '';
    if (this.entityType === 'team') {
      if (this.selectedTeamIds && this.selectedTeamIds.size > 0) {
        const names = Array.from(this.selectedTeamIds)
          .map(id => this.getTeamName(id))
          .filter(n => n)
          .join(', ');
        confirmHtml = `<p>You are about to update settings of <strong>${names}</strong> for <strong>${this.selectedEntities.size}</strong> employee(s).</p>`;
      } else {
        confirmHtml = `<p>You are about to update settings for <strong>${this.selectedEntities.size}</strong> ${this.entityType}(s).</p>`;
      }
    } else {
      confirmHtml = `<p>You are about to update settings for <strong>${this.selectedEntities.size}</strong> ${this.entityType}(s).</p>`;
    }

    Swal.fire({
      title: 'Confirm Bulk Update',
      html: confirmHtml,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, update',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkUpdate();
      }
    });
  }

  /**
   * Perform the actual bulk update API call
   */
  performBulkUpdate(): void {
    this.isSubmitting = true;
    if (!this.preparePayload()) {
      return;
    }

    let appliedSettings = {
      ...this.preparePayload(),
      ss_personalizeSettings: true
    }

    const payload = {
      team_id: this.selectedTeamId,
      employee_ids: Array.from(this.selectedEntities),
      settings: appliedSettings
    };

    this.projectService.bulkUpdateSettings(payload).subscribe(
      (response: any) => {
        this.isSubmitting = false;
        let successText = '';
        if (this.entityType === 'team') {
          if (this.selectedTeamIds && this.selectedTeamIds.size > 0) {
            const names = Array.from(this.selectedTeamIds)
              .map(id => this.getTeamName(id))
              .filter(n => n)
              .join(', ');
            successText = `Settings updated for ${this.selectedEntities.size} employee(s) in team(s) ${names}`;
          } else {
            successText = `Settings updated for ${this.selectedEntities.size} ${this.entityType}(s)`;
          }
        } else {
          successText = `Settings updated for ${this.selectedEntities.size} ${this.entityType}(s)`;
        }

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: successText,
          timer: 2000,
          showConfirmButton: false
        });

        // Reset form
        this.selectedEntities.clear();
        this.selectAllChecked = false;
        this.settingsForm.reset();
        this.createSettingsForm();
        this.isDirty = false;
        this.entityType = 'employee';
        this.loadEntities();
        this.snackBar.open('Bulk update completed successfully', 'Close', { duration: 3000 });
      },
      (error: any) => {
        this.isSubmitting = false;
        console.error('Bulk update error:', error);
        Swal.fire({
          icon: 'error',
          title: 'Update Failed',
          text: error.error?.message || 'An error occurred during bulk update',
          confirmButtonText: 'OK'
        });
      }
    );
  }

  /**
   * Switch between employees and teams
   */
  switchEntityType(type: 'employee' | 'team'): void {
    this.entityType = type;
    this.selectedEntities.clear();
    this.selectAllChecked = false;
    this.searchTerm = '';
    this.currentPage = 0;
    this.refreshBulkAddOnApplicability();
    this.loadEntities();
  }

  /**
   * Handler when a team is selected from dropdown
   */
  onTeamSelect(teamId: string): void {
    if (!teamId) return;
    this.selectedTeamId = teamId;

    // Find team and compute member ids to auto-select
    const team = this.allEntities.find(t => String(t.id) === String(teamId));
    let memberIds: string[] = [];
    if (team) {
      memberIds = this.parseTeamMetaMembers((team as any).meta || team);
      if ((memberIds == null || memberIds.length === 0) && Array.isArray((team as any).users) && (team as any).users.length) {
        memberIds = (team as any).users.map((u: any) => String(u.id || u));
      }
    }

    // Replace current selection with team member ids so members are selected by default
    this.selectedEntities.clear();
    memberIds.forEach(id => this.selectedEntities.add(String(id)));
    this.selectAllChecked = memberIds.length > 0;

    this.populateMembersForTeam(teamId);
    this.refreshBulkAddOnApplicability();
  }

  /**
   * Parse team.meta to extract unique member ids (managers, leads, developers)
   */
  private parseTeamMetaMembers(meta: any): string[] {
    if (!meta) return [];
    const collect = (val: any) => {
      if (!val) return [];
      try {
        return val
          .replace(/~$/g, '')
          .replace(/^~/g, '')
          .replaceAll('~', ',')
          .split(',')
          .map((s: string) => (s || '').trim())
          .filter((s: string) => s !== '' && s !== 'false');
      } catch (e) {
        return [];
      }
    };

    const managers = collect(meta.team_manager);
    const developers = collect(meta.team_developer);
    const leaders = collect(meta.team_lead);

    const all = [...managers, ...developers, ...leaders];
    return Array.from(new Set(all));
  }

  /**
   * Populate members for the selected team and replace the displayed entities with those members
   */
  populateMembersForTeam(teamId: string): void {
    const team = this.allEntities.find(t => t.id == teamId);
    if (!team) {
      this.teamMembersEntities = [];
      this.filteredEntities = [];
      this.updatePaginatedItems();
      return;
    }

    const memberIds = this.parseTeamMetaMembers((team as any).meta || team);

    const buildMembers = (users: any[]) => {
      const members: SelectableEntity[] = [];
      memberIds.forEach(id => {
        const u = users.find((x: any) => String(x.id) === String(id));
        if (u) {
          // Determine role from team's meta lists
          let role = '';
          const meta = (team as any).meta || team;
          if (meta && meta.team_manager && meta.team_manager.includes(id)) role = 'manager';
          else if (meta && meta.team_lead && meta.team_lead.includes(id)) role = 'lead';
          else if (meta && meta.team_developer && meta.team_developer.includes(id)) role = 'employee';

          members.push({
            id: String(u.id),
            name: u.name || u.email || '',
            role: role,
            profile_image_url: u.meta?.profile_image_url || u.profile_image_url || '',
            selected: this.selectedEntities.has(String(u.id))
          });
        }
      });

      // fallback: if team.users contains objects
      if (members.length === 0 && Array.isArray((team as any).users) && (team as any).users.length) {
        (team as any).users.forEach((u: any) => {
          const id = String(u.id || u);
          if (!members.find(m => m.id === id)) {
            members.push({ id, name: u.name || u.email || String(id), selected: this.selectedEntities.has(id) });
          }
        });
      }

      this.teamMembersEntities = members;
      this.filteredEntities = [...members];
      // remove others instead of checked from this.selectedEntities and it depends on members
      this.selectedEntities.forEach(id => {
        if (!members.find(m => m.id === id)) {
          this.selectedEntities.delete(id);
        }
      });

      this.currentPage = 0;
      this.updatePaginatedItems();
      this.refreshBulkAddOnApplicability();
    };

    if (this.cachedUsers && this.cachedUsers.length > 0) {
      buildMembers(this.cachedUsers);
      return;
    }

    // fetch users to resolve ids
    this.projectService.getUserList(0, 0, false).subscribe((users: any) => {
      this.cachedUsers = Array.isArray(users) ? users : users.data || [];
      buildMembers(this.cachedUsers);
    }, () => {
      // on error, still try to use team.users
      buildMembers([]);
    });
  }

  /**
   * Populate members for multiple selected teams (union of members)
   */
  populateMembersForTeams(teamIds: string[]): void {
    if (!teamIds || teamIds.length === 0) {
      // no teams selected -> show teams list again
      this.filteredEntities = [...this.allEntities];
      this.teamMembersEntities = [];
      this.currentPage = 0;
      this.updatePaginatedItems();
      return;
    }

    // collect member ids from each team
    const allMemberIds = new Set<string>();
    teamIds.forEach(id => {
      const t = this.allEntities.find(x => String(x.id) === String(id));
      if (t) {
        const ids = this.parseTeamMetaMembers((t as any).meta || t);
        ids.forEach((m: string) => allMemberIds.add(m));
      }
    });

    const memberIds = Array.from(allMemberIds);

    const buildMembers = (users: any[]) => {
      const members: SelectableEntity[] = [];
      memberIds.forEach(id => {
        const u = users.find((x: any) => String(x.id) === String(id));
        if (u) {
          members.push({
            id: String(u.id),
            name: u.name || u.email || '',
            profile_image_url: u.meta?.profile_image_url || u.profile_image_url || '',
            selected: this.selectedEntities.has(String(u.id))
          });
        }
      });

      this.teamMembersEntities = members;
      this.filteredEntities = [...members];
      this.currentPage = 0;
      this.updatePaginatedItems();
      this.refreshBulkAddOnApplicability();
    };

    if (this.cachedUsers && this.cachedUsers.length > 0) {
      buildMembers(this.cachedUsers);
      return;
    }

    this.projectService.getUserList(0, 0, false).subscribe((users: any) => {
      this.cachedUsers = Array.isArray(users) ? users : users.data || [];
      buildMembers(this.cachedUsers);
    }, () => {
      buildMembers([]);
    });
  }

  onTeamMultiSelectFromArray(selectedIds: string[]) {
    this.selectedTeamIds = new Set(selectedIds.map(s => String(s)));
    this.selectAllTeams = this.selectedTeamIds.size === this.allEntities.length;
    this.populateMembersForTeams(Array.from(this.selectedTeamIds));
  }

  onTeamMultiSelect(event: any): void {
    const opts = event.target && event.target.selectedOptions ? Array.from(event.target.selectedOptions) : [];
    const ids = opts.map((o: any) => o.value);
    this.onTeamMultiSelectFromArray(ids);
  }

  toggleSelectAllTeams(): void {
    if (!this.selectAllTeams) {
      // select all
      this.allEntities.forEach(e => this.selectedTeamIds.add(String(e.id)));
      this.selectAllTeams = true;
    } else {
      this.selectedTeamIds.clear();
      this.selectAllTeams = false;
    }
    this.populateMembersForTeams(Array.from(this.selectedTeamIds));
  }

  /**
   * Reset all settings to initial values
   */
  resetSettings(): void {
    this.settingsForm.reset(this.initialFormValue);
    this.isDirty = false;
    this.refreshBulkAddOnApplicability();
  }

  /**
   * Get selected entities count
   */
  getSelectedCount(): number {
    return this.selectedEntities.size;
  }

  /**
   * Get total entities count
   */
  getTotalCount(): number {
    return this.filteredEntities.length;
  }

  /**
   * Retrieve a team's display name by id
   */
  getTeamName(teamId: string): string {
    const team = this.allEntities.find(e => String(e.id) === String(teamId));
    return team ? team.name : '';
  }

  teamSelected(selectedTeam?: any) {
    if (selectedTeam) {
      // When a team is selected from UI, auto-select its members by default
      const team = this.allEntities.find(t => String(t.id) === String(selectedTeam));
      let memberIds: string[] = [];
      if (team) {
        memberIds = this.parseTeamMetaMembers((team as any).meta || team);
        if ((memberIds == null || memberIds.length === 0) && Array.isArray((team as any).users) && (team as any).users.length) {
          memberIds = (team as any).users.map((u: any) => String(u.id || u));
        }
      }

      this.selectedEntities.clear();
      memberIds.forEach(id => this.selectedEntities.add(String(id)));
      this.selectAllChecked = memberIds.length > 0;
      this.populateMembersForTeam(selectedTeam);
      this.refreshBulkAddOnApplicability();
    }
  }

  /**
   * Get selected user details by ID
   */
  getSelectedUserByIds(userId: string): SelectableEntity | undefined {
    // Search in all employees first
    let user = this.allEmployeeList.find((e: any) => String(e.id) === String(userId));
    if (user) {
      return {
        id: String(user.id),
        name: user.name || user.email || '',
        role: user.roles2 || user.role || '',
        profile_image_url: user.meta?.profile_image_url || user.profile_image_url || '',
      };
    }
    // If not found, search in all teams' members
    for (const team of this.allTeamsList) {
      if (team.users && Array.isArray(team.users)) {
        const member = team.users.find((u: any) => String(u.id) === String(userId));
        if (member) {
          return {
            id: String(member.id),
            name: member.name || member.email || '',
            role: member.roles2 || member.role || '',
            profile_image_url: member.meta?.profile_image_url || member.profile_image_url || '',
          };
        }
      }
    }
    // Fallback: search in filteredEntities
    return this.filteredEntities.find(e => String(e.id) === String(userId));
  }

  /**
   * Remove a user from selection
   */
  removeSelectedUser(userId: string): void {
    this.selectedEntities.delete(userId);
    this.updateSelectAllCheckbox();
    this.refreshBulkAddOnApplicability();
  }

  /**
   * Fetch and cache global settings
   */
  async getGlobalDataSettings() {
    this.projectService.getGlobalSettings().subscribe((res: any) => {
      this.globalSettingsData = (res && res.data) ? res.data : res;
      this.createSettingsForm();
    }, (error) => {
      console.log("Error while getting global data", error);
      this.createSettingsForm();
    });
  }

  /**
   * Retrieve all employees and store them for later use
   */
  getAllEmployees(): void {
    this.isLoadingEmployees = true;
    this.projectService.getUserList(0, 0, true).subscribe(
      (data: any) => {
        const list = Array.isArray(data) ? data : data.data || [];
        this.allEmployeeList = list;
        if (!this.isLead) {
          this.loadCurrentUser();
        }
        this.isLoadingEmployees = false;
      },
      (error) => {
        console.error('Failed to fetch all employees:', error);
        this.isLoadingEmployees = false;
      }
    );
  }

  /**
   * Retrieve all teams and store them for later use
   */
  getAllTeams(): void {
    this.projectService.getTeamList().subscribe(
      (data: any) => {
        const list = Array.isArray(data) ? data : data.data || [];
        this.allTeamsList = list;
      },
      (error) => {
        console.error('Failed to fetch all teams:', error);
      }
    );
  }
  /**
   * Expose Array for use in template *ngFor
   */
  Array = Array;
}
